import { useState, useRef, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import DashboardLayout from '../components/DashboardLayout';
import { Send, Database, Bot, User, Loader2, Plus, Trash2, MessageSquare } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { toast } from 'sonner';
import { useTierAccess } from '../hooks/useTierAccess';
import { UpgradePrompt } from '../components/UpgradePrompt';
import ReactMarkdown from 'react-markdown';

export default function DataAssistantPage() {
  const { api } = useAuth();
  const { canAccess, isAdmin } = useTierAccess();
  const hasAccess = isAdmin || canAccess('ai_chat');

  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const [sessions, setSessions] = useState([]);
  const messagesEndRef = useRef(null);

  useEffect(() => {
    if (hasAccess) fetchSessions();
  }, [hasAccess]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const fetchSessions = async () => {
    try {
      const res = await api().get('/data-assistant/sessions');
      setSessions(res.data.sessions || []);
    } catch { /* ignore */ }
  };

  const loadSession = async (sid) => {
    try {
      const res = await api().get(`/data-assistant/history?session_id=${sid}`);
      setSessionId(sid);
      setMessages((res.data.messages || []).map(m => ({
        role: m.role,
        content: m.content,
        datasets: m.datasets_used || [],
        dataQueried: m.data_queried || false,
      })));
    } catch {
      toast.error('Failed to load conversation');
    }
  };

  const deleteSession = async (sid) => {
    try {
      await api().delete(`/data-assistant/sessions/${sid}`);
      setSessions(s => s.filter(x => x.session_id !== sid));
      if (sessionId === sid) { setSessionId(null); setMessages([]); }
    } catch { toast.error('Failed to delete'); }
  };

  const startNewChat = () => {
    setSessionId(null);
    setMessages([]);
  };

  const sendMessage = async () => {
    if (!input.trim() || loading) return;
    const userMsg = input.trim();
    setInput('');
    setMessages(prev => [...prev, { role: 'user', content: userMsg }]);
    setLoading(true);

    try {
      const res = await api().post('/data-assistant', {
        message: userMsg,
        session_id: sessionId,
      });
      const { response, session_id, data_queried, datasets_used } = res.data;
      setSessionId(session_id);
      setMessages(prev => [...prev, {
        role: 'assistant',
        content: response,
        datasets: datasets_used || [],
        dataQueried: data_queried,
      }]);
      fetchSessions();
    } catch (err) {
      const detail = err.response?.data?.detail;
      if (typeof detail === 'object' && detail?.error === 'upgrade_required') {
        toast.error(detail.message);
      } else {
        toast.error(typeof detail === 'string' ? detail : 'Failed to get response');
      }
      setMessages(prev => prev.slice(0, -1));
    } finally {
      setLoading(false);
    }
  };

  if (!hasAccess) {
    return (
      <DashboardLayout>
        <div className="space-y-6" data-testid="data-assistant-page">
          <div className="mb-6">
            <h1 className="text-2xl font-bold text-white">Insite AI</h1>
            <p className="text-zinc-400">AI-powered queries against live NYC compliance data</p>
          </div>
          <div className="glass-card rounded-xl">
            <UpgradePrompt requiredTier="pro" feature="Insite AI" />
          </div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="h-[calc(100vh-8rem)] flex gap-4" data-testid="data-assistant-page">
        {/* Sidebar - Sessions */}
        <div className="hidden md:flex flex-col w-56 shrink-0">
          <Button onClick={startNewChat} className="btn-primary mb-3 w-full text-xs" data-testid="new-data-chat-btn">
            <Plus className="w-3.5 h-3.5 mr-1.5" /> New Chat
          </Button>
          <div className="flex-1 overflow-y-auto space-y-1">
            {sessions.map(s => (
              <div
                key={s.session_id}
                className={`group flex items-center gap-2 px-3 py-2 rounded-lg cursor-pointer text-xs transition-colors ${
                  sessionId === s.session_id ? 'bg-indigo-500/20 text-white' : 'text-zinc-400 hover:bg-white/5'
                }`}
                onClick={() => loadSession(s.session_id)}
              >
                <MessageSquare className="w-3 h-3 shrink-0" />
                <span className="flex-1 truncate">{s.preview}</span>
                <button
                  onClick={(e) => { e.stopPropagation(); deleteSession(s.session_id); }}
                  className="opacity-0 group-hover:opacity-100 transition-opacity"
                >
                  <Trash2 className="w-3 h-3 text-zinc-500 hover:text-rose-400" />
                </button>
              </div>
            ))}
          </div>
        </div>

        {/* Main Chat */}
        <div className="flex-1 flex flex-col glass-card rounded-xl overflow-hidden">
          {/* Header */}
          <div className="px-5 py-3 border-b border-white/5 flex items-center gap-3">
            <div className="p-1.5 bg-emerald-500/20 rounded-lg">
              <Database className="w-4 h-4 text-emerald-400" />
            </div>
            <div>
              <h2 className="text-sm font-semibold text-white">Insite AI</h2>
              <p className="text-[11px] text-zinc-500">Query live NYC compliance data</p>
            </div>
          </div>

          {/* Messages */}
          <div className="flex-1 overflow-y-auto px-5 py-4 space-y-4">
            {messages.length === 0 && (
              <div className="flex flex-col items-center justify-center h-full text-center">
                <Database className="w-10 h-10 text-emerald-400/30 mb-4" />
                <h3 className="text-sm font-medium text-zinc-300 mb-1">Ask anything about NYC building data</h3>
                <p className="text-xs text-zinc-500 max-w-sm mb-6">
                  Query live violation, permit, and compliance data using natural language
                </p>
                <div className="flex flex-wrap justify-center gap-2">
                  {[
                    'How many open violations at 250 W 34th St?',
                    'Show ECB violations for block 1305 lot 1 in Manhattan',
                    'What permits were filed for 120 Broadway?',
                  ].map((q, i) => (
                    <button
                      key={i}
                      onClick={() => { setInput(q); }}
                      className="px-3 py-1.5 text-[11px] text-zinc-400 bg-white/5 border border-white/10 rounded-full hover:bg-white/10 transition-colors"
                      data-testid={`suggestion-${i}`}
                    >
                      {q}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {messages.map((msg, i) => (
              <div key={i} className={`flex gap-3 ${msg.role === 'user' ? 'justify-end' : ''}`}>
                {msg.role === 'assistant' && (
                  <div className="w-7 h-7 rounded-lg bg-emerald-500/20 flex items-center justify-center shrink-0 mt-0.5">
                    <Bot className="w-4 h-4 text-emerald-400" />
                  </div>
                )}
                <div className={`max-w-[80%] ${msg.role === 'user'
                  ? 'bg-indigo-500/20 border border-indigo-500/20 rounded-2xl rounded-tr-sm px-4 py-2.5'
                  : 'bg-white/5 border border-white/5 rounded-2xl rounded-tl-sm px-4 py-3'
                }`}>
                  {msg.datasets?.length > 0 && (
                    <div className="flex flex-wrap gap-1.5 mb-2">
                      {msg.datasets.map(ds => (
                        <span key={ds} className="px-2 py-0.5 bg-emerald-500/10 border border-emerald-500/20 rounded-full text-[10px] text-emerald-300 font-medium">
                          {ds.replace(/_/g, ' ')}
                        </span>
                      ))}
                    </div>
                  )}
                  {msg.role === 'assistant' ? (
                    <div className="prose prose-invert prose-sm max-w-none text-sm text-zinc-300 [&_h2]:text-base [&_h2]:text-white [&_h3]:text-sm [&_h3]:text-zinc-200 [&_strong]:text-white [&_li]:text-zinc-300 [&_code]:text-emerald-300 [&_code]:bg-white/5 [&_code]:px-1 [&_code]:rounded">
                      <ReactMarkdown>{msg.content}</ReactMarkdown>
                    </div>
                  ) : (
                    <p className="text-sm text-white">{msg.content}</p>
                  )}
                </div>
                {msg.role === 'user' && (
                  <div className="w-7 h-7 rounded-lg bg-indigo-500/20 flex items-center justify-center shrink-0 mt-0.5">
                    <User className="w-4 h-4 text-indigo-400" />
                  </div>
                )}
              </div>
            ))}

            {loading && (
              <div className="flex gap-3">
                <div className="w-7 h-7 rounded-lg bg-emerald-500/20 flex items-center justify-center shrink-0">
                  <Bot className="w-4 h-4 text-emerald-400" />
                </div>
                <div className="bg-white/5 border border-white/5 rounded-2xl rounded-tl-sm px-4 py-3">
                  <div className="flex items-center gap-2 text-xs text-zinc-400">
                    <Loader2 className="w-3.5 h-3.5 animate-spin" />
                    Querying NYC datasets...
                  </div>
                </div>
              </div>
            )}
            <div ref={messagesEndRef} />
          </div>

          {/* Input */}
          <div className="px-5 py-3 border-t border-white/5">
            <form onSubmit={(e) => { e.preventDefault(); sendMessage(); }} className="flex gap-2">
              <Input
                value={input}
                onChange={(e) => setInput(e.target.value)}
                placeholder="Ask about violations, permits, or compliance data..."
                className="flex-1 bg-white/5 border-white/10 text-white text-sm"
                disabled={loading}
                data-testid="data-assistant-input"
              />
              <Button
                type="submit"
                disabled={loading || !input.trim()}
                className="bg-emerald-600 hover:bg-emerald-700 text-white"
                data-testid="data-assistant-send"
              >
                <Send className="w-4 h-4" />
              </Button>
            </form>
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
}
